import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { AutoLinkPlugin } from "@lexical/react/LexicalAutoLinkPlugin";
import { HistoryPlugin, type HistoryState } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { Accordion, AccordionDetails, AccordionSummary, Typography } from "@mui/material";
import { type EditorState } from "lexical";
import { useState, type FC, type RefObject } from "react";
import { useFlags } from "../../../server/optimizely";
import type { ChannelName } from "../../channels/types";
import { useAccountId } from "../../generic/hooks/use-account-id";
import type { ToolbarSettings } from "../../lexical-email-moment/hooks/use-email-toolbar-settings";
import type { MergeTagV1 } from "../../merge-tag/types";
import { useLexicalContext } from "./lexical-context";
import type { LexicalEditorProps } from "./lexical-editor";
import { BulletIndentStylePlugin } from "./plugins/bullet-indent-style-plugin";
import { ButtonListPlugin } from "./plugins/button-list-plugin";
import { DeleteNodePlugin } from "./plugins/delete-plugin";
import { DividerPlugin } from "./plugins/divider-plugin";
import { DragEventsPlugin } from "./plugins/drag-events-plugin";
import { DraggableBlockPlugin } from "./plugins/draggable-block-plugin";
import { EnsureTextWithinParagraphPlugin } from "./plugins/ensure-text-within-paragraph-plugin";
import { GenericFloatingToolbar } from "./plugins/generic-floating-toolbar/generic-floating-toolbar";
import { ImagePlugin } from "./plugins/image-plugin";
import { EditorInitializationPlugin } from "./plugins/initialise-on-change";
import { LinkPlugin } from "./plugins/link-plugin";
import { NoRootSelectionPlugin } from "./plugins/no-root-selection-plugin";
import { PastePlugin } from "./plugins/paste-plugin";
import { ReadOnlyPlugin } from "./plugins/read-only-plugin";
import { SingleTablePlugin } from "./plugins/single-table-plugin";
import { StylableButtonPlugin } from "./plugins/styleable-button-plugin";
import { TabIndentationPlugin } from "./plugins/tab-indent-plugin";
import { TableMenuPlugin } from "./plugins/table-menu-plugin";
import { LinkModalPlugin } from "./plugins/toolbar/link-modal-plugin";
import { SaveDHModalPlugin } from "./plugins/toolbar/save-dh-modal-plugin";
import { SaveImageModalPlugin } from "./plugins/toolbar/save-image-modal-plugin";
import { TreeViewPlugin } from "./plugins/tree-view-plugin";
import { BroadcastTypeaheadPlugin } from "./plugins/typeahead/broadcast-typeahead";
import { ChannelTypeaheadPlugin } from "./plugins/typeahead/channel-typeahead";
import { EmojiTypeaheadPlugin } from "./plugins/typeahead/emoji-typeahead";
import { MergeTagTypeaheadV2Plugin } from "./plugins/typeahead/merge-tag-typeahead";
import { FocusChangedPlugin } from "./plugins/use-focus-plugin";
import { VideoPlugin } from "./plugins/video-plugin";
import { linkMatchers } from "./utils/link-matchers";
import { useGetCharacterCount } from "./utils/use-get-character-count";
import { useRemoveTeamsChannelPollNodes } from "./utils/use-remove-teams-channel-poll-nodes";
import type { StyleableColumnConfig } from "./utils/use-styleable-columns";

export const DefaultBodyPlugins: FC<
  Pick<LexicalEditorProps, "format"> & {
    disabled?: boolean;
    channel: NonNullable<ChannelName>;
    onChange: (val: EditorState) => void;
    toolbarSettings?: ToolbarSettings;
    editorScrollRef?: RefObject<HTMLDivElement | null>;
    styleableColumnConfig?: StyleableColumnConfig | null;
    historyState: HistoryState;
    v1Tags: MergeTagV1[];
    auto_focus?: boolean;
  }
> = ({
  disabled,
  onChange,
  channel,
  format,
  editorScrollRef,
  toolbarSettings,
  styleableColumnConfig,
  historyState,
  v1Tags,
  auto_focus: autoFocus = true,
}) => {
  const accountId = useAccountId();
  const { flags, flagsLoaded } = useFlags("debug", "chat_references", "sms_improvements");
  const getCharacterCount = useGetCharacterCount();
  const [debugExpanded, setDebugExpanded] = useState(false);

  const {
    focus: { setFocused },
    drag: { setDraggedOver },
    upload: { setUploading },
    floatingAnchor: { floatingAnchorElem },
    text: { setCharacterCount },
  } = useLexicalContext();

  useRemoveTeamsChannelPollNodes();

  if (!flagsLoaded || !accountId) return null;

  return (
    <>
      <LinkPlugin />
      <DeleteNodePlugin />
      {format !== "sms" && (
        <>
          <ListPlugin />
          <TabIndentationPlugin />
          <DividerPlugin />
          <ImagePlugin
            onDragover={setDraggedOver}
            onUploading={setUploading}
            channel={channel}
            anchorElem={floatingAnchorElem ?? undefined}
          />
          <VideoPlugin channel={channel} anchorElem={floatingAnchorElem ?? undefined} />
        </>
      )}
      {autoFocus && <AutoFocusPlugin />}
      <AutoLinkPlugin matchers={linkMatchers} />
      <SaveImageModalPlugin disabled={!!disabled} />
      <SaveDHModalPlugin disabled={!!disabled} />
      <LinkModalPlugin disabled={!!disabled} />
      <EmojiTypeaheadPlugin />
      <MergeTagTypeaheadV2Plugin v1Tags={v1Tags} />
      <EnsureTextWithinParagraphPlugin />
      <OnChangePlugin
        onChange={(state) => {
          onChange(state);
          if (channel === "twilio" && flags.sms_improvements) {
            setCharacterCount(getCharacterCount(state));
          }
        }}
        ignoreSelectionChange={true}
      />
      <EditorInitializationPlugin onChange={onChange} />
      <HistoryPlugin externalHistoryState={historyState} />
      <FocusChangedPlugin onChange={setFocused} />

      {floatingAnchorElem && toolbarSettings?.floatingElements && (
        <GenericFloatingToolbar
          channel={toolbarSettings.channel}
          disabled={!!disabled}
          floatingElementsByNodeType={toolbarSettings.floatingElements}
          editorScrollRef={editorScrollRef}
        />
      )}
      <PastePlugin account_id={accountId} channel={channel} />
      {format === "email" ? (
        <>
          <StylableButtonPlugin />
          {floatingAnchorElem && (
            <>
              <SingleTablePlugin styleableColumnConfig={styleableColumnConfig} />
              <TablePlugin hasCellBackgroundColor={true} />
              <TableMenuPlugin anchorElem={floatingAnchorElem} />
              <BulletIndentStylePlugin />
            </>
          )}
        </>
      ) : format === "chat" ? (
        <>
          <ButtonListPlugin />
          {flags.chat_references && (
            <>
              <BroadcastTypeaheadPlugin />
              <ChannelTypeaheadPlugin />
            </>
          )}
        </>
      ) : null}
      <ReadOnlyPlugin readonly={!!disabled} />
      <DragEventsPlugin />
      <NoRootSelectionPlugin />
      {floatingAnchorElem && format !== "sms" && (
        <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
      )}

      {flags.debug && (
        <Accordion expanded={debugExpanded} onChange={() => setDebugExpanded((x) => !x)}>
          <AccordionSummary>
            <Typography variant="h6">Debug</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TreeViewPlugin />
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};
